(function() {
    'use strict';
    /*
     * Copyright (c) 2015 In Hand Health.
     * All rights reserved.
     */

    angular
        .module('ihh-common-web')
        .directive('ihhViewEditText', ihhViewEditText);

    /**
     * A directive for displaying a text field in read mode and changing it to a text input in edit mode. Intended for use with
     * controllers that derive from {@link ihh-common-web.ViewEditCtrlBase}.
     * @ngdoc directive
     * @name ihh-view-edit-text
     * @memberOf ihh-common-web
     * @example
     * <ihh-view-edit-text
     *    control-id="some-id"
     *    edit-flag="isEdit"
     *    is-required="true"
     *    label="Name"
     *    model="foo.name"
     *    model-edit="fooTemp.name">
     * </ihh-view-edit-text>
     */
    function ihhViewEditText() {
        return {
            restrict: 'AE',
            transclude: true,
            scope: {
                /**
                 * The HTML id to use for the control and label, for accessibility.
                 * @ngdoc property
                 * @name control-id
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                controlId: '@',

                /**
                 * The text to display as a label.
                 * @ngdoc property
                 * @name label
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                label: '@',

                /**
                 * The text to display as a secondary label.
                 * @ngdoc property
                 * @name secondary label
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                secondaryLabel: '@',

                /**
                 * An expression for determining whether or not to hide the label
                 * @ngdoc property
                 * @name hide-label
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                hideLabel: '=',

                /**
                 * The condition to cause the editable element to focus.
                 * @ngdoc property
                 * @name focus
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                focus: '=',

                /**
                 * An expression for determining whether this element should be marked as valid.
                 * @ngdoc property
                 * @name valid-if-exp
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                validIfExp: '=',

                /**
                 * An expression for determining whether this element should be disabled.
                 * @ngdoc property
                 * @name disabled-if-exp
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                disabledIfExp: '=',

                /**
                 * If set, marks the field as required, and in edit mode displayes a visual cue that the value is required.
                 * @ngdoc property
                 * @name is-required
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                isRequired: '=',

                /**
                 * If set, overrides the HTML 'type' attribute on the input element. Allows for password, email, etc.
                 * @ngdoc property
                 * @name text-type
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                textType: '@',

                /**
                 * If set, configures a min value for the input
                 * @ngdoc property
                 * @name number-min
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                numberMin: '@',

                /**
                 * If set, configures a max value for the input
                 * @ngdoc property
                 * @name number-max
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                numberMax: '@',

                /**
                 * The step attribute specifies the legal number intervals for an input element.
                 * @ngdoc property
                 * @name step
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                step: '@',

                /**
                 * The scope variable used to track view and edit mode.
                 * @ngdoc property
                 * @name edit-flag
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                editFlag: '=',

                /**
                 * The scope variable used as the model for displaying the text in view mode.
                 * @ngdoc property
                 * @name model
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                model: '=',

                /**
                 * The scope variable used as the model for the input field when in edit mode.
                 * @ngdoc property
                 * @name model-edit
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                modelEdit: '=',

                /**
                 * The scope variable used as the model for the input field default.
                 * @ngdoc property
                 * @name model-default
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                modelDefault: '=',

                /**
                 * If set, a function to format the model value in view mode.
                 */
                viewFormatFn: '=',

                /**
                 * The scope variable used to determine inline style
                 * @ngdoc property
                 * @name inline-style
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                inlineStyle: '=',

                /**
                 * The scope variable used to display help text next to labels.
                 * @ngdoc property
                 * @name help-text
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                helpText: '@',

                /**
                 * The scope variable used to display help symbols next to labels.
                 * @ngdoc property
                 * @name help-symbol
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                helpSymbol: '@',

                /**
                 * The scope variable used to validate the input against a regular expression
                 * The default pattern is "at least one character". Meaning that a required field
                 * must contain one character, OR match the supplied pattern.
                 * @ngdoc property
                 * @name pattern
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                pattern: '@',

                /**
                 * If truthy, indicates that a numeric zero value should be considered set and not show the naText in view mode.
                 * @ngdoc property
                 * @name zeroIsSet
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                zeroIsSet: '@',

                /**
                 * The scope variable used to display a different 'N/A' text
                 * @ngdoc property
                 * @name na-text
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                naText: '@',

                /**
                 * The scope variable used to display a placeholder
                 * @ngdoc property
                 * @name placeholder
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                placeholder: '@',

                /**
                 * The scope variable used to display a mask
                 * @ngdoc property
                 * @name mask
                 * @memberOf ihh-common-web.ihh-view-edit-text
                 */
                mask: '@'
            },
            templateUrl: 'view-edit/view-edit-text.tpl.html',
            link: function(scope /*, elem, attrs*/ ) {
                if (!scope.textType) {
                    scope.textType = 'text';
                }

                if (!scope.naText) {
                    scope.naText = 'N/A';
                }

                /**
                 * Returns whether the value is considered set for the purpose of deciding whether or not to show the N/A text.
                 * @param {any} value the value to test
                 * @returns {boolean} true if the value is considered set; false otherwise
                 */
                scope.isSet = function(value) {
                    return value || (scope.zeroIsSet && value === 0);
                };
            }
        };
    }
})();